import { hash } from '@ember/helper';
import { or, and } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';

export interface TioClickableTextSignature {
  Args: {
    ariaLabel?: string;
    disabled?: boolean;
    href?: string;
    icon?: unknown;
    iconClass?: string;
    iconLeft?: boolean;
    iconRight?: boolean;
    linkTo?: string;
    linkToModel?: string | number | null;
    onClick?: (event?: Event) => void;
    outlined?: boolean;
    query?: Record<string, unknown>;
    textClass?: string;
  };
  Element: HTMLElement;
  Blocks: {
    default: [];
  };
}

export default class TioClickableText extends Component<TioClickableTextSignature> {
  get baseClass() {
    const padding = this.args.icon ? 'px-2 py-2 rounded-full' : 'px-3 py-2';
    const defaultSize = this.args.icon ? 'h-8 w-8 text-lg' : '';
    return `focus-visible:ring focus-visible:ring-offset-2 text-center flex items-center gap-2 items-center justify-center border relative overflow-hidden ${padding} ${defaultSize}`;
  }

  getColorShade(shade: string) {
    return this.args.disabled ? 'neutral-300' : `ocean-${shade}`;
  }

  get primaryColor() {
    return this.getColorShade('600');
  }

  get hoverColor() {
    return this.getColorShade('800');
  }

  get outlinedClass() {
    const primary = this.primaryColor;
    const hover = this.hoverColor;
    return `${this.baseClass} border-${primary} hover:border-${hover} bg-white text-${primary} hover:text-${hover} ${this.args.iconClass}`;
  }

  get solidClass() {
    const primary = this.primaryColor;
    const hover = this.hoverColor;
    return `${this.baseClass} border-${primary} hover:border-${hover} bg-${primary} hover:bg-${hover} text-white ${this.args.iconClass}`;
  }

  get textClass() {
    const primary = this.primaryColor;
    const hover = this.hoverColor;
    const baseTextClass = `text-${primary} hover:text-${hover} [&>a]:text-[var(--color-ocean-600)] [&>a]:hover:text-[var(--color-ocean-800)] [&>a]:underline [&>a]:font-semibold [&>span]:text-[var(--color-ocean-600)] [&>span]:hover:text-[var(--color-ocean-800)] [&>span]:underline [&>span]:font-semibold`;
    return this.args.textClass ? `${baseTextClass} ${this.args.textClass}` : baseTextClass;
  }

  get iconClass() {
    return this.args.outlined ? this.outlinedClass : this.solidClass;
  }

  <template>
    <div ...attributes>
      {{#if (and @linkTo @linkToModel)}}
        <LinkTo
          @route={{@linkTo}}
          @model={{@linkToModel}}
          @query={{or @query (hash)}}
          aria-label={{@ariaLabel}}
        >
          <div class="flex items-center gap-2">
            {{#if @icon}}
              {{#if @iconLeft}}
                <@icon class={{this.iconClass}} />
              {{/if}}
            {{/if}}
            <div class={{this.textClass}}>
              {{yield}}
            </div>
            {{#if @icon}}
              {{#if @iconRight}}
                <@icon class={{this.iconClass}} />
              {{/if}}
            {{/if}}
          </div>
        </LinkTo>
      {{else if @linkTo}}
        <LinkTo @route={{@linkTo}} @query={{or @query (hash)}} aria-label={{@ariaLabel}}>
          <div class="flex items-center gap-2">
            {{#if @icon}}
              {{#if @iconLeft}}
                <@icon class={{this.iconClass}} />
              {{/if}}
            {{/if}}
            <div class={{this.textClass}}>
              {{yield}}
            </div>
            {{#if @icon}}
              {{#if @iconRight}}
                <@icon class={{this.iconClass}} />
              {{/if}}
            {{/if}}
          </div>
        </LinkTo>
      {{else if @href}}
        <a href={{@href}} aria-label={{@ariaLabel}}>
          <div class="flex items-center gap-2">
            {{#if @icon}}
              {{#if @iconLeft}}
                <@icon class={{this.iconClass}} />
              {{/if}}
            {{/if}}
            <div class={{this.textClass}}>
              {{yield}}
            </div>
            {{#if @icon}}
              {{#if @iconRight}}
                <@icon class={{this.iconClass}} />
              {{/if}}
            {{/if}}
          </div>
        </a>
      {{else}}
        {{#if @onClick}}
          <button
            type="button"
            disabled={{or @disabled}}
            {{on "click" @onClick}}
            aria-label={{@ariaLabel}}
          >
            <div class="flex items-center gap-2">
              {{#if @icon}}
                {{#if @iconLeft}}
                  <@icon class={{this.iconClass}} />
                {{/if}}
              {{/if}}
              <div class={{this.textClass}}>
                {{yield}}
              </div>
              {{#if @icon}}
                {{#if @iconRight}}
                  <@icon class={{this.iconClass}} />
                {{/if}}
              {{/if}}
            </div>
          </button>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
